/**
 * Generates client-side HTML preview of the unsubscribe feedback page.
 * Matches the visual style of packages/unsubscribe/src/lib/feedback-page.ts.
 */

const BRAND_ORANGE = '#FF6B00'
const BRAND_CREAM = '#FEFCFA'
const BRAND_DARK = '#1A1A1A'
const BRAND_GRAY = '#6B7280'
const BRAND_LIGHT_GRAY = '#E5E7EB'

const STAMP_SVG = `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
	<rect width="40" height="40" rx="8" fill="${BRAND_ORANGE}"/>
	<path d="M10 14L20 22L30 14" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
	<path d="M10 14H30V26C30 27.1 29.1 28 28 28H12C10.9 28 10 27.1 10 26V14Z" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

type PreviewParams = {
  audienceName: string
  unsubMessage: string
  reasons: string[]
  includeOther: boolean
}

export function buildUnsubPreviewHtml(params: PreviewParams): string {
  const { audienceName, unsubMessage, reasons, includeOther } = params

  const message = unsubMessage.trim()
    ? escapeHtml(unsubMessage.trim())
    : `You won&#39;t receive any more emails from ${escapeHtml(audienceName)}.`

  const allReasons = [...reasons.map((r) => r.trim()).filter(Boolean)]
  if (includeOther) allReasons.push('Other')

  const reasonCheckboxes = allReasons
    .map(
      (reason) =>
        `<label class="reason-label"><input type="checkbox" disabled> ${escapeHtml(reason)}</label>`,
    )
    .join('\n\t\t\t')

  const otherTextarea = includeOther
    ? `<textarea class="feedback-input" placeholder="Tell us more..." disabled></textarea>`
    : ''

  return `<!DOCTYPE html>
<html lang="en">
<head>
	<meta charset="utf-8">
	<meta name="viewport" content="width=device-width, initial-scale=1">
	<title>Unsubscribe Preview — One-Email</title>
	<link rel="preconnect" href="https://fonts.googleapis.com">
	<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
	<link href="https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;600;700&display=swap" rel="stylesheet">
	<style>
		*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
		body {
			font-family: 'Bricolage Grotesque', system-ui, -apple-system, sans-serif;
			background: ${BRAND_CREAM}; color: ${BRAND_DARK};
			min-height: 100vh; display: flex; align-items: center;
			justify-content: center; padding: 24px;
		}
		.card {
			background: white; border-radius: 16px;
			box-shadow: 0 1px 3px rgba(0,0,0,0.08), 0 4px 24px rgba(0,0,0,0.04);
			padding: 40px; max-width: 440px; width: 100%;
		}
		.preview-badge {
			background: ${BRAND_ORANGE}; color: white; font-size: 11px;
			font-weight: 600; padding: 4px 10px; border-radius: 999px;
			display: inline-block; margin-bottom: 16px; text-transform: uppercase;
			letter-spacing: 0.5px;
		}
		.logo { margin-bottom: 24px; display: inline-block; text-align: center; width: 100%; }
		h1 { font-size: 20px; font-weight: 700; line-height: 1.4; margin-bottom: 8px; text-align: center; }
		.subtitle { color: ${BRAND_GRAY}; font-size: 15px; line-height: 1.5; margin-bottom: 24px; text-align: center; }
		.divider { border: none; border-top: 1px solid ${BRAND_LIGHT_GRAY}; margin: 24px 0; }
		.prompt { font-size: 15px; font-weight: 600; margin-bottom: 16px; }
		.reason-label {
			display: flex; align-items: center; gap: 10px;
			padding: 10px 12px; border: 1px solid ${BRAND_LIGHT_GRAY};
			border-radius: 8px; margin-bottom: 8px; cursor: default;
			font-size: 14px;
		}
		.reason-label input[type="checkbox"] {
			width: 18px; height: 18px; accent-color: ${BRAND_ORANGE}; flex-shrink: 0;
		}
		.feedback-input {
			width: 100%; padding: 10px 12px; border: 1px solid ${BRAND_LIGHT_GRAY};
			border-radius: 8px; font-family: inherit; font-size: 14px;
			margin-top: 4px; resize: vertical; min-height: 60px;
		}
		.btn-primary {
			display: block; width: 100%; padding: 14px 24px;
			background: ${BRAND_ORANGE}; color: white; font-family: inherit;
			font-size: 15px; font-weight: 600; border: none;
			border-radius: 10px; cursor: default; margin-top: 20px; opacity: 0.7;
		}
		.skip-link {
			display: block; text-align: center; margin-top: 16px;
			color: ${BRAND_GRAY}; font-size: 13px; text-decoration: underline;
			text-underline-offset: 2px;
		}
		.footer { color: ${BRAND_GRAY}; font-size: 12px; margin-top: 24px; text-align: center; }
	</style>
</head>
<body>
	<main class="card" role="main">
		<div style="text-align:center"><span class="preview-badge">Preview</span></div>
		<div class="logo" aria-hidden="true">${STAMP_SVG}</div>
		<h1>You've been unsubscribed</h1>
		<p class="subtitle">${message}</p>
		<hr class="divider">
		<p class="prompt">Would you mind letting us know why?</p>
		${reasonCheckboxes}
		${otherTextarea}
		<button type="button" class="btn-primary" disabled>Send Feedback</button>
		<span class="skip-link">No thanks</span>
		<hr class="divider">
		<p class="footer">Powered by One-Email</p>
	</main>
</body>
</html>`
}

export function openUnsubPreview(params: PreviewParams): void {
  const html = buildUnsubPreviewHtml(params)
  const blob = new Blob([html], { type: 'text/html' })
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank')
}
